import { ThemeEvents } from '@theme/events';

/**
 * <variant-metafield-content>
 *
 * Makes an accordion row's Variant-metafield content variant-aware. The server preloads every
 * variant's HTML (snippets/accordion-variant-metafield.liquid); this element swaps the visible
 * body to the new variant's HTML on Horizon's native `variant:update` event — no extra network
 * request, matching how the rest of the PDP (media, price, inventory) reacts to variant changes.
 *
 * Why listen on `document`: the native variant-aware components listen on their closest
 * `.shopify-section` because they live INSIDE the product section that dispatches the event.
 * The Universal Accordion is its own section, so it can't scope to that section — the event
 * bubbles to `document` (VariantUpdateEvent is dispatched with bubbles: true), and we guard by
 * matching `detail.data.productId` so we never react to an unrelated product's variant change.
 */
class VariantMetafieldContent extends HTMLElement {
  connectedCallback() {
    this.body = this.querySelector('[data-variant-mf-body]');

    const dataEl = this.querySelector('[data-variant-mf-data]');
    try {
      this.variantMap = dataEl ? JSON.parse(dataEl.textContent) : {};
    } catch {
      this.variantMap = {};
    }

    this.row = this.closest('.accordion__row');
    // In the theme editor, never hide rows/blocks — merchants must be able to see and select
    // them even when the previewed variant resolves blank. Content still swaps for preview.
    this.designMode = Boolean(window.Shopify && window.Shopify.designMode);
    // When the metafield is the row's ONLY content, an empty value should collapse the whole
    // row (like a variant that has no value for this field). Otherwise just this block hides.
    this.soleSource = this.#isSoleSource();

    // Reflect the server-rendered initial state (selected variant may resolve blank while
    // others have a value — then we start hidden until a populated variant is selected).
    this.#applyVisibility(this.#hasBody());

    this.onVariantUpdate = this.onVariantUpdate.bind(this);
    document.addEventListener(ThemeEvents.variantUpdate, this.onVariantUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.variantUpdate, this.onVariantUpdate);
  }

  #hasBody() {
    return Boolean(this.body && this.body.innerHTML.trim() !== '');
  }

  /** True when this variant metafield is the only meaningful content in the row. */
  #isSoleSource() {
    const content = this.closest('.accordion__content');
    if (!content) return false;
    const clone = content.cloneNode(true);
    clone.querySelectorAll('variant-metafield-content').forEach((el) => el.remove());
    return clone.textContent.trim() === '' && clone.querySelector('img, a, iframe, video, svg') === null;
  }

  /** @param {boolean} hasContent */
  #applyVisibility(hasContent) {
    const show = hasContent || this.designMode;
    this.toggleAttribute('hidden', !show);
    if (this.soleSource && this.row) {
      this.row.toggleAttribute('hidden', !show);
    }
  }

  /** @param {Event & { detail?: { resource?: { id?: number }, data?: { productId?: string, newProduct?: { id?: string } } } }} event */
  onVariantUpdate(event) {
    const detail = event.detail || {};
    const resource = detail.resource;
    const data = detail.data || {};
    if (!resource) return;

    // Only react to THIS product. A combined-listing switch to a different product can't be
    // served from our preloaded map, so clear out rather than show a stale value.
    const myProductId = this.dataset.productId;
    const eventProductId = data.newProduct ? data.newProduct.id : data.productId;
    if (myProductId && eventProductId && String(eventProductId) !== String(myProductId)) {
      if (this.body) this.body.innerHTML = '';
      this.#applyVisibility(false);
      return;
    }

    const html = this.variantMap[String(resource.id)] || '';
    if (this.body) this.body.innerHTML = html;
    this.#applyVisibility(html.trim() !== '');
  }
}

if (!customElements.get('variant-metafield-content')) {
  customElements.define('variant-metafield-content', VariantMetafieldContent);
}
